// Balances.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import { Wallet, RefreshCw, Clock, Info } from 'lucide-react';
import { dashboardService, DashboardStats } from '../services/dashboardService';
import { getMerchantBalances } from '../services/transferService';
import LoadingScreen from './LoadingScreen';
import WalletDetailsModal, { WalletDetailsData } from './dashboard/WalletDetailsModal';

interface OperatorBalance {
  method: string;
  currency: string;
  depositBalance: number;
  withdrawalBalance: number;
  reservedDeposit: number;
  reservedWithdrawal: number;
  available: number;
  inFlight: number;
  total: number;
}

const Balances: React.FC = () => {
  const { user } = useAuth();
  const { t, countryConfig } = useLocalization();
  const [selectedCurrency, setSelectedCurrency] = useState('CDF');
  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState<OperatorBalance[]>([]);
  const [, setDashboardData] = useState<DashboardStats | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [showDetails, setShowDetails] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState<OperatorBalance | null>(null);

  const currencies = ['CDF', 'USD'];

  const loadBalances = async () => {
    if (!user?.merchant_code) return;

    try {
      setLoading(true);

      const [stats] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getOperatorStats()
      ]);

      setDashboardData(stats);

      const operatorBalances: OperatorBalance[] = [];

      for (const operator of countryConfig.operators) {
        const normalizedMethod = operator.toLowerCase() === 'm-pesa' ? 'mpesa' : operator.toLowerCase();

        try {
          const balanceData = await getMerchantBalances(
            user.merchant_code,
            selectedCurrency,
            normalizedMethod
          );

          operatorBalances.push({
            method: operator,
            currency: selectedCurrency,
            depositBalance: balanceData.deposit_balance || 0,
            withdrawalBalance: balanceData.withdrawal_balance || 0,
            reservedDeposit: balanceData.reserved_deposit || 0,
            reservedWithdrawal: balanceData.reserved_withdrawal || 0,
            available: balanceData.available || 0,
            inFlight: balanceData.in_flight || 0,
            total: balanceData.total_balance || 0,
          });
        } catch (error) {
          console.warn(`Failed to load balance for ${operator} (${selectedCurrency}):`, error);
          operatorBalances.push({
            method: operator,
            currency: selectedCurrency,
            depositBalance: 0,
            withdrawalBalance: 0,
            reservedDeposit: 0,
            reservedWithdrawal: 0,
            available: 0,
            inFlight: 0,
            total: 0,
          });
        }
      }

      setBalances(operatorBalances);
      setLastUpdated(new Date());

    } catch (error) {
      console.error('Error loading balances:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBalances();
  }, [user?.merchant_code, selectedCurrency]);

  const totalAvailable = balances.reduce((sum, b) => sum + b.available, 0);
  const totalInFlight = balances.reduce((sum, b) => sum + b.inFlight, 0);
  const totalAll = balances.reduce((sum, b) => sum + b.total, 0);

  const fmt = (n: number) =>
    new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const getOperatorColor = (operator: string) => {
    const colors: { [key: string]: string } = {
      'Orange': '#FF8C00',
      'Airtel': '#E53E3E',
      'M-Pesa': '#00A651',
      'Afrimoney': '#6B46C1',
      'MTN': '#FFCC00',
      'Vodacom': '#E60000',
      'Moov': '#00A651',
      'Safaricom': '#00A651',
      'Telkom': '#0066CC',
      'Free': '#C41E3A',
      'Expresso': '#FF6600',
      'Malitel': '#0066CC',
      'Vodafone': '#E60000',
      'AirtelTigo': '#E53E3E',
      'Africell': '#00A651',
      'Tigo': '#0066CC',
      'Halotel': '#FF6600',
      'Telecel': '#0066CC'
    };
    return colors[operator] || '#6B46C1';
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />

        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 mb-3">
              <Wallet className="w-3 h-3 text-emerald-400" />
              <span className="text-[11px] font-semibold text-emerald-300 tracking-wider uppercase">{t('balances.title')}</span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight">{t('balances.title')}</h1>
            <p className="text-sm text-slate-400 mt-1">{t('balances.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl p-1">
              {currencies.map((currency) => (
                <button
                  key={currency}
                  onClick={() => setSelectedCurrency(currency)}
                  className={`px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    selectedCurrency === currency
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {currency}
                </button>
              ))}
            </div>
            <button
              onClick={loadBalances}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl text-sm font-medium text-slate-300 hover:bg-white/10 transition-all duration-200 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Consolidated summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 block">{t('balances.available') || 'Disponible'}</span>
              <span className="text-[10px] text-gray-400">{selectedCurrency}</span>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {loading ? '...' : fmt(totalAvailable)}
          </p>
          <p className="text-xs text-emerald-500 mt-1">{t('balances.availableHint') || 'Utilisable maintenant'}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 block">{t('balances.inFlight') || 'Réservé'}</span>
              <span className="text-[10px] text-gray-400">{selectedCurrency}</span>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {loading ? '...' : fmt(totalInFlight)}
          </p>
          <p className="text-xs text-amber-500 mt-1">{t('balances.inFlightHint') || 'Bloqué pour des paiements ou transferts en cours'}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-lg shadow-slate-700/20">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 block">{t('balances.total') || 'Total'}</span>
              <span className="text-[10px] text-gray-400">{selectedCurrency}</span>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {loading ? '...' : fmt(totalAll)}
          </p>
          <p className="text-xs text-slate-500 mt-1">{t('balances.totalHint') || 'Disponible + En cours'}</p>
        </div>
      </div>

      {/* Operator Balances */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Wallet className="h-4.5 w-4.5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">{t('balances.operators')}</h2>
                <p className="text-xs text-gray-500">{t('balances.subtitle')} - {selectedCurrency}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-emerald-600 transition-colors"
              >
                <Info className="h-3.5 w-3.5" />
                {showDetails ? (t('balances.hideDetails') || 'Masquer le détail') : (t('balances.showDetails') || 'Voir le détail')}
              </button>
              <span className="text-xs text-gray-400">{lastUpdated.toLocaleTimeString()}</span>
            </div>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <LoadingScreen fullscreen={false} message={t('balances.loading')} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {balances.map((balance, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => setSelectedBalance(balance)}
                  className="text-left w-full rounded-xl p-4 border border-gray-100 bg-gray-50/50 hover:shadow-md hover:border-emerald-200 transition-all duration-200 group relative"
                >
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-1 text-[9px] text-gray-500 bg-white border border-gray-200 rounded-full px-1.5 py-0.5">
                      <Info className="w-2.5 h-2.5" />
                      <span>{t('balances.clickForDetails') || 'Détails'}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full ring-2 ring-white shadow-sm"
                        style={{ backgroundColor: getOperatorColor(balance.method) }}
                      />
                      <h3 className="font-semibold text-gray-900 text-sm">{balance.method}</h3>
                    </div>
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                      {balance.currency}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <p className="text-[11px] text-gray-500 uppercase tracking-wide">
                        {t('balances.available') || 'Disponible'}
                      </p>
                      <p className="text-base font-bold text-emerald-700 group-hover:text-emerald-800 transition-colors">
                        {fmt(balance.available)}
                      </p>
                    </div>

                    {balance.inFlight > 0 && (
                      <div className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1">
                        <Clock className="h-3 w-3" />
                        <span>{fmt(balance.inFlight)} {t('balances.inFlight') || 'réservé'}</span>
                      </div>
                    )}

                    {showDetails && (
                      <div className="pt-2 border-t border-gray-200/60 space-y-1">
                        <div className="flex justify-between text-[11px]">
                          <span className="text-gray-500">Pay-In</span>
                          <span className="text-gray-700">{fmt(balance.depositBalance)}</span>
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span className="text-gray-500">Pay-Out</span>
                          <span className="text-gray-700">{fmt(balance.withdrawalBalance)}</span>
                        </div>
                        {(balance.reservedDeposit > 0 || balance.reservedWithdrawal > 0) && (
                          <div className="flex justify-between text-[11px]">
                            <span className="text-gray-500">{t('balances.reserved') || 'Réservé'}</span>
                            <span className="text-amber-600">
                              {fmt(balance.reservedDeposit + balance.reservedWithdrawal)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between text-[11px] pt-1 border-t border-gray-200/60">
                          <span className="font-medium text-gray-600">{t('balances.total') || 'Total'}</span>
                          <span className="font-bold text-gray-900">{fmt(balance.total)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <WalletDetailsModal
        data={
          selectedBalance
            ? ({
                name: selectedBalance.method,
                currency: selectedBalance.currency,
                deposit: selectedBalance.depositBalance,
                withdrawal: selectedBalance.withdrawalBalance,
                reservedDeposit: selectedBalance.reservedDeposit,
                reservedWithdrawal: selectedBalance.reservedWithdrawal,
                available: selectedBalance.available,
                inFlight: selectedBalance.inFlight,
                total: selectedBalance.total,
                iconColor: getOperatorColor(selectedBalance.method),
              } as WalletDetailsData)
            : null
        }
        onClose={() => setSelectedBalance(null)}
      />
    </div>
  );
};

export default Balances;
